import { reaction } from "mobx";
import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { StorageModel } from "../storage/StorageModel";
import { ActivityTypes } from "./ActivityPricesStorage";
import { authentication } from "../authentication/Authentication";
import { currentUserService } from "../user/CurrentUserStorage";
import { translationService } from "../translation/TranslationService";

const CreditBalanceModel = types.model({
    balance: types.optional(types.frozen<number>(), -1)
})

export const CreditBalanceStorageModel = types
    .compose(CreditBalanceModel, StorageModel)
    .named("CreditBalanceStorageModel")
    .actions((self) => {
        return {
            mapResponse(response: any) {
                // Tolerate any response shape — null, number, PascalCase keys, etc.
                // If we can't read item1/item2, set balance to 0 so isLoading clears.
                // Without this, an unexpected response left balance at -1, hanging the splash.
                const item1 = Number(response?.item1 ?? response?.Item1 ?? 0);
                const item2 = Number(response?.item2 ?? response?.Item2 ?? 0);
                self.balance = (Number.isFinite(item1) ? item1 : 0) - (Number.isFinite(item2) ? item2 : 0);
            },
            getCustomerPortalSessionLink: flow(function* () {
                const endpoint = endpoints.get("shop_orders");
                endpoint.queryParams = "language=" + translationService.language;
                return yield getEnv(self).api.get(endpoint);
            }),
            validateRemainingBalance: flow(function* (activities: ActivityTypes[]) {
                const endpoint = endpoints.get("validate_user_balance");
                endpoint.queryParams = "services=" + activities.join("&services=");
                try {
                    yield getEnv(self).api.get(endpoint);
                    return true;
                } catch (error) {
                    return false;
                }
            }),
            mockup() {
                self.balance = 0;
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            return endpoints.get("current_user_balance");
        },
        get isLoading() {
            // See StorageModel.loadAttempted. This store's canLoadItems needs a loaded
            // currentUser, so without the flag a stalled user load leaves it "loading" forever.
            if (self.error || self.loadAttempted) return false;
            // -1 is the "not yet loaded" sentinel set in the model default.
            // Any other value (including 0 or a real negative balance) means loaded.
            return self.balance === -1;
        },
        get canLoadItems() {
            const env: any = getEnv(self);
            return (env?.currentUserService?.currentUser?.id ?? -1) > -1;
        },
    }))

export const creditBalanceStorage = CreditBalanceStorageModel.create({ autoLoad: false }, { api, currentUserService });
export type CreditBalanceStorage = Instance<typeof CreditBalanceStorageModel>;

reaction(
    () => currentUserService.currentUser.id,
    async () => {
      if (authentication.anonymous) {
        creditBalanceStorage.mockup();
      } else {
        await creditBalanceStorage.load();
      }
    }
  );
  
reaction(
    () => authentication.anonymous,
    async () => {
      if (!authentication.anonymous && currentUserService.currentUser.id > -1) await creditBalanceStorage.load();
    }
  )